import {
  ColumnBlock,
  ColumnContentTemplate,
  ColumnSubtaskTemplate,
  ContentTemplate,
  ContentType,
  IExperienceTemplateResource,
  LayoutBlockType,
  SubTaskType,
  TaskBlockDenormalized,
  TaskBlockTemplate
} from '@dadohr/shared/domainTypes/experience';
import { Integration } from '@dadohr/shared/domainTypes/integrations';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { ScheduleChannel } from '@dadohr/shared/domainTypes/schedules';
import styled from '@emotion/styled';
import { IconButton, MenuItem, Select, Slider } from '@material-ui/core';
import { capitalize } from 'lodash';
import { ChevronLeft, Edit, PanelLeft, PanelRight } from 'lucide-react';
import { SetStateAction, useMemo, useState } from 'react';
import {
  BlockDefinition,
  ContentBlockSettingsItem,
  SubTaskBlockSettingsItem,
  useContentBlocks,
  useSubTaskBlocks
} from '../../..';
import { CustomInputLabel } from '../../../../../../../../components/CustomInputLabel';
import { InfoBox } from '../../../../../../../../components/InfoBox';
import { TokenDefinition } from '../../../../../../../../components/MarkdownText';
import { useUnsafeTranslation } from '../../../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical,
  FlexItem
} from '../../../../../../../../layout/Flex';
import { useIntegrationsContext } from '../../../../../../../../services/integrations/context';
import { AnchorField } from '../../../../../Anchors';
import { BlockSettingsSubPage, TaskBuilderContext } from '../../../types';
import {
  TaskBuilderBlockValidationState,
  TaskBuilderBlockValidationStateItem,
  useFieldValidator,
  VALIDATORS_TEXT
} from '../../../validation';

export const isValidColumnContent = (
  t: TaskBlockTemplate | TaskBlockDenormalized
): t is ColumnContentTemplate => {
  return (
    (t.type as any) !== LayoutBlockType.COLUMNS &&
    (Object.values(ContentType).includes(t.type as any) ||
      t.type === SubTaskType.CONFIRM ||
      t.type === SubTaskType.VISIT)
  );
};

export const isContentColumnTemplate = (
  t: ColumnContentTemplate
): t is ContentTemplate => Object.values(ContentType).includes(t.type as any);

export const isSubtaskColumnTemplate = (
  t: ColumnContentTemplate
): t is ColumnSubtaskTemplate =>
  t.type === SubTaskType.CONFIRM || t.type === SubTaskType.VISIT;

const TypeSelector = ({
  value,
  onChange,
  subtaskPasThroughProps
}: {
  value: ColumnContentTemplate;
  onChange: (nextTpl: ColumnContentTemplate) => void;

  subtaskPasThroughProps?: {
    resources: IExperienceTemplateResource[];
    onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  };
}) => {
  const contentBlocks = useContentBlocks({ extendedSelection: true });

  const subTaskBlocks = useSubTaskBlocks(
    subtaskPasThroughProps?.resources || [],
    subtaskPasThroughProps?.onChangeResources || (() => {})
  );

  const allowedBlocks = useMemo(() => {
    const allblocks: BlockDefinition<TaskBlockTemplate>[] = [
      ...contentBlocks,
      ...(subtaskPasThroughProps ? subTaskBlocks : [])
    ];
    return allblocks.filter(
      (b): b is BlockDefinition<ColumnContentTemplate> => {
        return (
          Object.values(ContentType).includes(b.type as any) ||
          b.type === SubTaskType.CONFIRM ||
          b.type === SubTaskType.VISIT
        );
      }
    );
  }, [contentBlocks, subTaskBlocks, subtaskPasThroughProps]);
  return (
    <Select
      fullWidth
      value={value.type}
      onChange={(ev) => {
        const nextType = ev.target.value as ContentType;
        if (value.type === nextType) {
          return;
        }
        const nextBlock = allowedBlocks.find((b) => b.type === nextType);
        if (nextBlock) {
          // onDeleteBlock(value.id);
          // onAddBlock(nextBlock.emptyBlock());
          onChange({
            ...nextBlock.emptyBlock(),
            parentLayoutElement: value.parentLayoutElement,
            id: value.id
          });
        }
      }}
    >
      {allowedBlocks.map((b) => (
        <MenuItem key={b.key} value={b.type}>
          <FlexContainer>
            <b.icon size={16} />
            {b.label}
          </FlexContainer>
        </MenuItem>
      ))}
    </Select>
  );
};

const Grid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: '1fr min-content',
  gridColumnGap: p.theme.spacing(1),
  width: '100%'
}));

const ColumnSelectorAndEditor = ({
  label,
  value,
  onChange,
  passThroughProps,
  subtaskPasThroughProps
}: {
  label: React.ReactNode;
  value: ColumnContentTemplate;
  onChange: (nextCol: ColumnContentTemplate) => void;
  passThroughProps: {
    validation: TaskBuilderBlockValidationStateItem | null;
    setValidation: React.Dispatch<
      SetStateAction<TaskBuilderBlockValidationState>
    >;
    customFields: CustomUserField[];
    expTpl: TaskBuilderContext;
    tokens: TokenDefinition[];
  };
  subtaskPasThroughProps?: {
    anchorFields: AnchorField[];
    customFields: CustomUserField[];
    goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
    showOnCompleteHookSettings: boolean;
    hideOptionalSubTaskSetting: boolean;
    actor: string;
    channels: ScheduleChannel[];
    resources: IExperienceTemplateResource[];
    onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  };
}) => {
  const [edit, setEdit] = useState(false);
  return (
    <CustomInputLabel
      label={
        edit ? (
          <FlexContainer>
            <IconButton onClick={() => setEdit(false)}>
              <ChevronLeft size={14} />
            </IconButton>
            {label}
          </FlexContainer>
        ) : (
          label
        )
      }
      fullWidth
    >
      {edit ? (
        <FlexItem flex={1}>
          {isContentColumnTemplate(value) ? (
            <ContentBlockSettingsItem
              value={value}
              onChange={onChange as any}
              {...passThroughProps}
            />
          ) : isSubtaskColumnTemplate(value) && subtaskPasThroughProps ? (
            <SubTaskBlockSettingsItem
              value={value}
              onChange={onChange as any}
              {...passThroughProps}
              {...subtaskPasThroughProps}
            />
          ) : null}
        </FlexItem>
      ) : (
        <Grid>
          <TypeSelector
            value={value}
            onChange={onChange}
            subtaskPasThroughProps={subtaskPasThroughProps}
          />
          <IconButton onClick={() => setEdit(true)}>
            <Edit size={16} />
          </IconButton>
        </Grid>
      )}
    </CustomInputLabel>
  );
};

export const BlockSettingsLayoutColumns = ({
  value,
  blocks,
  onChange,
  validation,

  setValidation,
  customFields,
  expTpl,
  tokens,
  subtaskPasThroughProps
}: {
  value: ColumnBlock;
  blocks: TaskBlockTemplate[];
  onChange: (nextValue: TaskBlockTemplate) => void;

  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  customFields: CustomUserField[];
  expTpl: TaskBuilderContext;
  tokens: TokenDefinition[];

  subtaskPasThroughProps?: {
    anchorFields: AnchorField[];
    customFields: CustomUserField[];
    goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
    showOnCompleteHookSettings: boolean;
    hideOptionalSubTaskSetting: boolean;
    actor: string;
    channels: ScheduleChannel[];
    resources: IExperienceTemplateResource[];
    onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  };
}) => {
  console.log('COLUMN BLOCK', value);
  const { active } = useIntegrationsContext();
  const { t } = useUnsafeTranslation();
  const validateColumns = useFieldValidator(
    setValidation,
    VALIDATORS_TEXT,
    'text'
  );
  const invalidTextReason = validation?.fields.text || null;
  const passThroughProps = {
    validation,
    setValidation,
    customFields,
    expTpl,
    tokens
  };

  const weightValues: [
    [number, number],
    [number, number],
    [number, number],
    [number, number],
    [number, number]
  ] = [
    [15, 85],
    [33, 66],
    [50, 50],
    [66, 33],
    [85, 15]
  ];

  const weightsFromSliderValue = (value: number) => {
    if (value < 0) {
      return weightValues[0];
    }
    if (value > 4) {
      return weightValues[4];
    }
    const normalizedValue = (Math.floor(value) % 5) as 0 | 1 | 2 | 3 | 4;
    return weightValues[normalizedValue];
  };

  const col1Value = blocks
    .filter(isValidColumnContent)
    .find((b) => b.id === value.data.columns[0]?.blockId);
  const col2Value = blocks
    .filter(isValidColumnContent)
    .find((b) => b.id === value.data.columns[1]?.blockId);

  const sliderValueFromWeights = (
    weights: [number, number]
  ): 0 | 1 | 2 | 3 | 4 => {
    const ratio = weights[0] / weights[1];
    const ratios = weightValues.map((w) => w[0] / w[1]);
    const index = ratios.findIndex((r) => ratio <= r);
    return index < 0 ? 4 : ((index % 5) as 0 | 1 | 2 | 3 | 4);
  };

  const inserIndex = blocks.findIndex((b) => b.id === value.id);

  return (
    <FlexContainerVertical spacing={2} fullWidth>
      <ColumnSelectorAndEditor
        label={'Left column'}
        value={col1Value!}
        onChange={onChange}
        passThroughProps={passThroughProps}
        subtaskPasThroughProps={subtaskPasThroughProps}
      />
      <ColumnSelectorAndEditor
        label={'Right column'}
        value={col2Value!}
        onChange={onChange}
        passThroughProps={passThroughProps}
        subtaskPasThroughProps={subtaskPasThroughProps}
      />
      <CustomInputLabel label={capitalize(t('Size'))} fullWidth>
        <PanelLeft size={24} strokeWidth={2} />
        <Slider
          marks
          aria-label={t('Column Size')}
          min={0}
          max={4}
          step={1}
          defaultValue={2}
          value={sliderValueFromWeights(
            value.data.columns.map((c) => c.columnWeight) as [number, number]
          )}
          onChange={(ev, nextSliderValue: number | number[]) => {
            const columnsWeights = weightsFromSliderValue(
              nextSliderValue as number
            );
            value.data.columns.forEach((c, i) => {
              c.columnWeight = columnsWeights[i];
            });

            onChange({
              ...value,
              data: {
                ...value.data
              }
            });
          }}
        />
        <PanelRight size={24} strokeWidth={2} />
      </CustomInputLabel>
      {active.has(Integration.SLACK) && (
        <InfoBox color="gray">
          {t(
            'Columns are not supported in Slack notifications. Column content will be rendered vertically, rather than side-by-side.'
          )}
        </InfoBox>
      )}
    </FlexContainerVertical>
  );
};

export const BlockSettingsLayoutColumnsReadOnly = ({
  value
}: {
  value: ColumnBlock;
}) => null;
